import React, { useState, useEffect } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, Globe, Coins, Sun, Moon, User, LogIn, LogOut, ClipboardList, ChevronDown } from "lucide-react";
import { useApp } from "../context/AppContext";
import { useCustomerAuth } from "../context/CustomerAuthContext";
import FloatingWhatsApp from "./FloatingWhatsApp";
import RecentBookings from "./RecentBookings";
import ScrollToTop from "./ScrollToTop";

const Layout = () => {
    const { lang, switchLang, t, settings, currency, switchCurrency, CURRENCY_SYMBOLS, theme, switchTheme } = useApp();
    const { customer, logout } = useCustomerAuth();
    const [open, setOpen] = useState(false);
    const [curOpen, setCurOpen] = useState(false);
    const [userOpen, setUserOpen] = useState(false);
    const [servicesOpen, setServicesOpen] = useState(false);
    const location = useLocation();

    // Hotel detail: immersive full-page — header hidden, revealed on scroll-up
    const isHotelDetail = /^\/hotels\/.+/.test(location.pathname);
    const [showHeader, setShowHeader] = useState(true);
    useEffect(() => {
        if (!isHotelDetail) { setShowHeader(true); return undefined; }
        setShowHeader(false);
        let lastY = window.scrollY;
        const onScroll = () => {
            const y = window.scrollY;
            if (y < lastY - 4) setShowHeader(true);
            else if (y > lastY + 4) setShowHeader(false);
            lastY = y;
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, [isHotelDetail, location.pathname]);

    const serviceLinks = [
        { to: "/transfers", label: t("nav.transfers") },
        { to: "/flights", label: t("nav.flights") },
        { to: "/visa", label: t("nav.visa") },
        { to: "/hac", label: t("nav.hajj") },
        { to: "/guides", label: t("nav.guides") },
    ];

    const links = [
        { to: "/", label: t("nav.home") },
        { to: "/#hotels", label: t("nav.hotels") },
        { to: "/emlak", label: t("nav.realestate") },
        { to: "/map", label: t("nav.map") },
        { to: "/contact", label: t("nav.contact") },
    ];

    const isServiceActive = serviceLinks.some((s) => location.pathname.startsWith(s.to));

    return (
        <div className="min-h-screen flex flex-col bg-emerald_deep text-bone">
            <header
                className={`z-50 glass border-b border-border_green ${
                    isHotelDetail
                        ? `fixed top-0 left-0 right-0 transition-transform duration-300 ${showHeader ? "translate-y-0" : "-translate-y-full"}`
                        : "sticky top-0"
                }`}
                data-testid="site-header"
            >
                <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3 flex-shrink-0" data-testid="brand-link">
                        <img src="/assets/asis-icon.png" alt="ASİS HOTELS" className="w-12 h-12 object-contain" />
                        <div className="leading-tight">
                            <div className="font-serif text-lg tracking-wider font-semibold text-gold">{t("brand")}</div>
                            <div className="text-[10px] uppercase tracking-[0.25em] text-muted_green">
                                Makkah · Madinah
                            </div>
                        </div>
                    </Link>

                    <nav className="hidden xl:flex flex-1 items-center justify-center gap-3 2xl:gap-6 px-4">
                        {links.slice(0, 2).map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.to === "/"}
                                className={({ isActive }) =>
                                    `text-[12px] xl:text-[13px] uppercase tracking-[0.12em] 2xl:tracking-[0.14em] whitespace-nowrap transition-colors ${
                                        isActive && location.pathname === l.to.split("#")[0]
                                            ? "text-gold"
                                            : "text-bone hover:text-gold"
                                    }`
                                }
                                data-testid={`nav-${l.to.replace(/[#/]/g, "") || "home"}-link`}
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        <div
                            className="relative"
                            onMouseEnter={() => setServicesOpen(true)}
                            onMouseLeave={() => setServicesOpen(false)}
                        >
                            <button
                                type="button"
                                onClick={() => setServicesOpen((s) => !s)}
                                className={`flex items-center gap-1 text-[12px] xl:text-[13px] uppercase tracking-[0.12em] 2xl:tracking-[0.14em] whitespace-nowrap transition-colors ${
                                    isServiceActive ? "text-gold" : "text-bone hover:text-gold"
                                }`}
                                data-testid="nav-services-btn"
                            >
                                {t("nav.services")} <ChevronDown className={`w-3 h-3 transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
                            </button>
                            {servicesOpen && (
                                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 z-50" data-testid="services-dropdown">
                                    <div className="bg-emerald_surface border border-border_green min-w-[190px] shadow-xl">
                                        {serviceLinks.map((s) => (
                                            <NavLink
                                                key={s.to}
                                                to={s.to}
                                                onClick={() => setServicesOpen(false)}
                                                className={({ isActive }) =>
                                                    `block px-4 py-3 text-[11px] uppercase tracking-[0.15em] hover:bg-emerald_hover border-b border-border_green last:border-b-0 ${
                                                        isActive ? "text-gold" : "text-bone"
                                                    }`
                                                }
                                                data-testid={`nav-service-${s.to.replace("/", "")}-link`}
                                            >
                                                {s.label}
                                            </NavLink>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        {links.slice(2).map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                className={({ isActive }) =>
                                    `text-[12px] xl:text-[13px] uppercase tracking-[0.12em] 2xl:tracking-[0.14em] whitespace-nowrap transition-colors ${
                                        isActive && location.pathname === l.to.split("#")[0]
                                            ? "text-gold"
                                            : "text-bone hover:text-gold"
                                    }`
                                }
                                data-testid={`nav-${l.to.replace(/[#/]/g, "") || "home"}-link`}
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        <NavLink
                            to="/track"
                            className={({ isActive }) =>
                                `text-center leading-tight whitespace-nowrap transition-colors ${isActive ? "text-gold" : "text-bone hover:text-gold"}`
                            }
                            data-testid="nav-track-link"
                        >
                            <span className="block text-[9px] uppercase tracking-[0.16em] opacity-75">{t("nav.track_line1")}</span>
                            <span className="block text-[12px] xl:text-[13px] uppercase tracking-[0.12em] 2xl:tracking-[0.14em]">{t("nav.track_short")}</span>
                        </NavLink>
                    </nav>

                    <div className="flex items-center gap-3 flex-shrink-0">
                        {/* Theme switcher */}
                        <button
                            onClick={() => switchTheme("toggle")}
                            className="hidden md:flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-bone/80 hover:text-gold transition-colors px-3 py-2 border border-border_green"
                            title={theme === "dark" ? "Açık temaya geç" : "Koyu temaya geç"}
                            data-testid="theme-toggle-btn"
                        >
                            {theme === "dark" ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
                        </button>
                        {/* Currency switcher */}
                        <div className="hidden xl:block relative">
                            <button
                                onClick={() => setCurOpen(!curOpen)}
                                onBlur={() => setTimeout(() => setCurOpen(false), 150)}
                                className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-bone/80 hover:text-gold transition-colors px-2.5 py-2 border border-border_green"
                                data-testid="currency-toggle-btn"
                            >
                                <Coins className="w-3.5 h-3.5" />
                                {currency}
                            </button>
                            {curOpen && (
                                <div className="absolute right-0 mt-1 bg-emerald_surface border border-border_green min-w-[120px] z-50">
                                    {["SAR", "TRY", "USD"].map((c) => (
                                        <button
                                            key={c}
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => { switchCurrency(c); setCurOpen(false); }}
                                            className={`block w-full text-left px-4 py-2.5 text-xs uppercase tracking-[0.2em] hover:bg-emerald_hover ${currency === c ? "text-gold" : "text-bone"}`}
                                            data-testid={`currency-option-${c}`}
                                        >
                                            {CURRENCY_SYMBOLS[c]} · {c}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <button
                            onClick={() => switchLang(lang === "tr" ? "en" : "tr")}
                            className="hidden xl:flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-bone/80 hover:text-gold transition-colors px-2.5 py-2 border border-border_green"
                            data-testid="language-toggle-btn"
                        >
                            <Globe className="w-3.5 h-3.5" />
                            {lang === "tr" ? "EN" : "TR"}
                        </button>
                        <Link to="/" onClick={(e) => e.stopPropagation()} className="hidden" />
                        {/* Customer auth menu replaces admin link in public nav */}
                        {customer ? (
                            <div className="hidden xl:block relative">
                                <button
                                    onClick={() => setUserOpen(!userOpen)}
                                    onBlur={() => setTimeout(() => setUserOpen(false), 180)}
                                    className="btn-outline-gold !text-xs !py-2"
                                    data-testid="user-menu-btn"
                                >
                                    <User className="w-3.5 h-3.5" /> {customer.name.split(" ")[0]}
                                </button>
                                {userOpen && (
                                    <div className="absolute right-0 mt-1 bg-emerald_surface border border-border_green min-w-[200px] z-50">
                                        <Link to="/profile" onMouseDown={(e) => e.preventDefault()} className="flex items-center gap-2 px-4 py-3 text-xs uppercase tracking-[0.18em] hover:bg-emerald_hover" data-testid="user-menu-profile">
                                            <ClipboardList className="w-3.5 h-3.5 text-gold" /> {t("nav.profile")}
                                        </Link>
                                        <button onMouseDown={(e) => e.preventDefault()} onClick={() => { logout(); setUserOpen(false); }} className="w-full flex items-center gap-2 px-4 py-3 text-xs uppercase tracking-[0.18em] hover:bg-emerald_hover text-left border-t border-border_green" data-testid="user-menu-logout">
                                            <LogOut className="w-3.5 h-3.5 text-rose-400" /> {t("nav.logout")}
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="hidden xl:flex items-center gap-2">
                                <Link to="/login" className="text-xs uppercase tracking-[0.14em] text-bone/85 hover:text-gold transition-colors whitespace-nowrap" data-testid="header-login-link">
                                    {t("nav.login")}
                                </Link>
                                <Link to="/register" className="btn-gold !text-xs !py-2 !px-3 whitespace-nowrap" data-testid="header-register-link">
                                    <User className="w-3.5 h-3.5" /> {t("nav.register")}
                                </Link>
                            </div>
                        )}
                        <button
                            className="xl:hidden text-bone"
                            onClick={() => setOpen(!open)}
                            data-testid="mobile-menu-toggle"
                            aria-label="Menu"
                        >
                            {open ? <X /> : <Menu />}
                        </button>
                    </div>
                </div>
                {open && (
                    <div className="xl:hidden bg-emerald_surface border-t border-border_green">
                        <div className="px-6 py-6 flex flex-col gap-4">
                            {links.slice(0, 2).map((l) => (
                                <Link
                                    key={l.to}
                                    to={l.to}
                                    onClick={() => setOpen(false)}
                                    className="text-sm uppercase tracking-[0.2em]"
                                    data-testid={`mobile-nav-${l.to.replace(/[#/]/g, "") || "home"}`}
                                >
                                    {l.label}
                                </Link>
                            ))}
                            <div className="border-l-2 border-gold/40 pl-3">
                                <div className="text-[10px] uppercase tracking-[0.25em] text-gold mb-2">{t("nav.services")}</div>
                                <div className="flex flex-col gap-3">
                                    {serviceLinks.map((s) => (
                                        <Link
                                            key={s.to}
                                            to={s.to}
                                            onClick={() => setOpen(false)}
                                            className="text-sm uppercase tracking-[0.2em]"
                                            data-testid={`mobile-nav-${s.to.replace("/", "")}`}
                                        >
                                            {s.label}
                                        </Link>
                                    ))}
                                </div>
                            </div>
                            {links.slice(2).map((l) => (
                                <Link
                                    key={l.to}
                                    to={l.to}
                                    onClick={() => setOpen(false)}
                                    className="text-sm uppercase tracking-[0.2em]"
                                    data-testid={`mobile-nav-${l.to.replace(/[#/]/g, "") || "home"}`}
                                >
                                    {l.label}
                                </Link>
                            ))}
                            <Link to="/track" onClick={() => setOpen(false)} className="text-sm uppercase tracking-[0.2em]" data-testid="mobile-nav-track">
                                {t("nav.track")}
                            </Link>
                            <button
                                onClick={() => switchLang(lang === "tr" ? "en" : "tr")}
                                className="text-xs uppercase tracking-[0.2em] text-gold text-left"
                                data-testid="mobile-language-toggle"
                            >
                                {lang === "tr" ? "EN" : "TR"} · Globe
                            </button>
                            <div className="flex gap-2 flex-wrap">
                                {["SAR", "TRY", "USD"].map((c) => (
                                    <button
                                        key={c}
                                        onClick={() => switchCurrency(c)}
                                        className={`text-xs uppercase tracking-[0.2em] px-3 py-1.5 border ${currency === c ? "border-gold text-gold" : "border-border_green text-bone/70"}`}
                                        data-testid={`mobile-currency-${c}`}
                                    >
                                        {c}
                                    </button>
                                ))}
                                <button
                                    onClick={() => switchTheme("toggle")}
                                    className="text-xs uppercase tracking-[0.2em] px-3 py-1.5 border border-border_green text-bone/70 flex items-center gap-1"
                                    data-testid="mobile-theme-toggle"
                                >
                                    {theme === "dark" ? <Sun className="w-3 h-3" /> : <Moon className="w-3 h-3" />}
                                    {theme === "dark" ? (lang === "en" ? "Light" : "Açık") : (lang === "en" ? "Dark" : "Koyu")}
                                </button>
                            </div>
                            {customer ? (
                                <div className="flex gap-2 flex-wrap pt-3 border-t border-border_green">
                                    <Link to="/profile" onClick={() => setOpen(false)} className="btn-outline-gold !text-xs w-fit" data-testid="mobile-profile-link">
                                        <User className="w-3.5 h-3.5" /> {t("nav.profile")}
                                    </Link>
                                    <button onClick={() => { logout(); setOpen(false); }} className="text-xs uppercase tracking-[0.18em] text-rose-400 hover:underline flex items-center gap-1.5" data-testid="mobile-logout-btn">
                                        <LogOut className="w-3.5 h-3.5" /> {t("nav.logout")}
                                    </button>
                                </div>
                            ) : (
                                <div className="flex gap-2 flex-wrap pt-3 border-t border-border_green">
                                    <Link to="/login" onClick={() => setOpen(false)} className="btn-outline-gold !text-xs w-fit" data-testid="mobile-login-link">
                                        <LogIn className="w-3.5 h-3.5" /> {t("nav.login")}
                                    </Link>
                                    <Link to="/register" onClick={() => setOpen(false)} className="btn-gold !text-xs w-fit" data-testid="mobile-register-link">
                                        <User className="w-3.5 h-3.5" /> {t("nav.register")}
                                    </Link>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </header>

            <main className="flex-1">
                <Outlet />
            </main>

            <footer className="border-t border-border_green bg-emerald_surface mt-20">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-3 gap-10">
                    <div>
                        <div className="flex items-center gap-3 mb-4">
                            <img src="/assets/asis-icon.png" alt="ASİS HOTELS" className="w-12 h-12 object-contain" />
                            <span className="font-serif text-xl font-semibold text-gold tracking-wider">{t("brand")}</span>
                        </div>
                        <p className="text-sm text-muted_green leading-relaxed">{t("tagline")}</p>
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-[0.25em] text-gold mb-4">{t("footer.quick")}</div>
                        <ul className="space-y-3 text-sm">
                            {[...links, ...serviceLinks, { to: "/track", label: t("nav.track") }].map((l) => (
                                <li key={l.to}>
                                    <Link to={l.to} className="text-bone/80 hover:text-gold">
                                        {l.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-[0.25em] text-gold mb-4">{t("footer.contact_us")}</div>
                        <ul className="space-y-3 text-sm text-bone/80">
                            <li>WhatsApp: +{settings.whatsapp_number || "—"}</li>
                            <li>{settings.contact_email}</li>
                            <li>{settings.company_name}</li>
                        </ul>
                    </div>
                </div>
                <div className="border-t border-border_green py-6 flex items-center justify-between gap-3 flex-wrap text-xs text-muted_green tracking-wider">
                    <span>© {new Date().getFullYear()} {t("brand")}. {t("footer.rights")}</span>
                    <Link
                        to="/admin/login"
                        className="text-[10px] uppercase tracking-[0.2em] opacity-50 hover:opacity-100 hover:text-gold transition-opacity"
                        data-testid="footer-admin-link"
                    >
                        Yönetici Girişi
                    </Link>
                </div>
            </footer>
            <ScrollToTop />
            <FloatingWhatsApp />
            <RecentBookings />
        </div>
    );
};

export default Layout;
